import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Wheat, Utensils, ExternalLink, Apple, Shirt, Users, X, ChevronDown, ChevronUp, MapPin } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function TripList({ rows, formatDate, accent = "amber", subtitleNote }) {
  if (!rows.length) return <div className="py-10 text-center text-muted-foreground text-sm">No trip days configured.</div>;
  const totalOff = rows.reduce((s, r) => s + (r.is_camp_day ? r.off_count : 0), 0);
  const totalRemaining = rows.reduce((s, r) => s + (r.is_camp_day ? r.remaining : 0), 0);
  return (
    <div className="space-y-3" data-testid={`triplist-${accent}`}>
      {subtitleNote && (
        <div className={`rounded-lg bg-${accent}-50 border border-${accent}-200 p-2.5 text-[11px] text-${accent}-900 flex items-start gap-2`}>
          <MapPin className={`w-3.5 h-3.5 text-${accent}-700 flex-shrink-0 mt-0.5`} />
          <span>{subtitleNote}</span>
        </div>
      )}

      <div className="grid grid-cols-3 gap-2">
        <div className={`rounded-lg bg-${accent}-50 border border-${accent}-200 p-2.5 text-center`}>
          <p className={`text-xl font-extrabold text-${accent}-800 leading-tight`}>{rows.filter(r => r.is_camp_day).length}</p>
          <p className={`text-[10px] text-${accent}-700 font-semibold`}>trip days (camp open)</p>
        </div>
        <div className={`rounded-lg bg-rose-50 border border-rose-200 p-2.5 text-center`}>
          <p className="text-xl font-extrabold text-rose-800 leading-tight">{totalOff.toLocaleString()}</p>
          <p className="text-[10px] text-rose-700 font-semibold">total campers off-site</p>
        </div>
        <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-2.5 text-center">
          <p className="text-xl font-extrabold text-emerald-800 leading-tight">{totalRemaining.toLocaleString()}</p>
          <p className="text-[10px] text-emerald-700 font-semibold">total kitchen-prep</p>
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 overflow-hidden">
        <div className={`bg-${accent}-100 px-3 py-1.5 text-[11px] font-bold text-${accent}-900 grid grid-cols-12 gap-2`}>
          <div className="col-span-5">Date</div>
          <div className="col-span-3 text-right">Going on Trip</div>
          <div className="col-span-2 text-right">Remaining</div>
          <div className="col-span-2 text-right">Total Day</div>
        </div>
        <div className="divide-y divide-slate-100">
          {rows.map(row => (
            <div
              key={row.date}
              className={`grid grid-cols-12 gap-2 items-center px-3 py-2 ${row.is_camp_day ? "hover:bg-amber-50" : "bg-slate-50/60 opacity-60"}`}
              data-testid={`trip-${accent}-${row.date}`}
            >
              <div className="col-span-5">
                <p className="text-sm font-semibold text-slate-800">{formatDate(row.date)}</p>
                <p className="text-[10px] text-muted-foreground font-mono">
                  {row.date}{!row.is_camp_day && " · weekend/closed"}{row.weekday && row.is_camp_day && ` · ${row.weekday}`}
                </p>
                <p className={`text-[10px] text-${accent}-700`}>{row.groups_label}</p>
              </div>
              <div className="col-span-3 text-right">
                <p className="text-lg font-extrabold text-rose-700">{row.off_count.toLocaleString()}</p>
              </div>
              <div className="col-span-2 text-right">
                <p className={`text-lg font-extrabold ${row.is_camp_day ? "text-emerald-700" : "text-slate-400"}`}>
                  {row.is_camp_day ? row.remaining.toLocaleString() : "—"}
                </p>
              </div>
              <div className="col-span-2 text-right">
                <p className={`text-sm font-bold ${row.is_camp_day ? "text-slate-700" : "text-slate-400"}`}>
                  {row.is_camp_day ? row.total_expected.toLocaleString() : "—"}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function DailyTotalsModal({ token, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showWeeks, setShowWeeks] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/dietary/daily-expected`, { headers: { Authorization: `Bearer ${token}` } });
        if (alive) setData(res.data);
      } catch {
        if (alive) setData({ days: [] });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [token]);

  const formatDate = (iso) => {
    const [y, m, d] = iso.split("-").map(n => parseInt(n, 10));
    const dt = new Date(y, m - 1, d);
    const wkday = dt.toLocaleString("en-US", { weekday: "short" });
    return `${wkday}, ${m}/${d}`;
  };

  // Group by week for the breakdown
  const weeks = (() => {
    if (!data?.days?.length) return [];
    const wks = [];
    let current = null;
    for (const row of data.days) {
      const [y, m, d] = row.date.split("-").map(n => parseInt(n, 10));
      const dt = new Date(y, m - 1, d);
      const monday = new Date(dt);
      monday.setDate(dt.getDate() - dt.getDay() + 1);
      const wkKey = monday.toISOString().slice(0, 10);
      if (!current || current.weekKey !== wkKey) {
        current = { weekKey: wkKey, label: `Week of ${monday.getMonth() + 1}/${monday.getDate()}`, rows: [] };
        wks.push(current);
      }
      current.rows.push(row);
    }
    return wks;
  })();

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dietary-daily-totals-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-800">
            <Users className="w-5 h-5" /> Daily Total Campers
          </DialogTitle>
          <DialogDescription>
            Expected camper counts for every camp day. Weekends and July 3rd excluded.
            Numbers honour flex schedules — only campers actually here on that date are counted.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-12 text-center text-muted-foreground">Loading…</div>
        ) : !data?.days?.length ? (
          <div className="py-12 text-center text-muted-foreground">No data available.</div>
        ) : (
          <div className="space-y-3 mt-2">
            <Tabs defaultValue="all" className="w-full">
              <TabsList className="grid grid-cols-4 w-full">
                <TabsTrigger value="all" data-testid="diet-tab-all">All Days</TabsTrigger>
                <TabsTrigger value="mariners" data-testid="diet-tab-mariners">Mariner Trips</TabsTrigger>
                <TabsTrigger value="cits" data-testid="diet-tab-cits">CIT Trips</TabsTrigger>
                <TabsTrigger value="navigators" data-testid="diet-tab-navigators">Nav Trips</TabsTrigger>
              </TabsList>

              {/* ── All Days ── */}
              <TabsContent value="all" className="mt-3">
                <button
                  type="button"
                  onClick={() => setShowWeeks(v => !v)}
                  className="w-full flex items-center justify-between text-xs font-bold text-amber-800 hover:text-amber-900 py-1"
                  data-testid="dietary-daily-totals-toggle"
                >
                  <span>Daily breakdown</span>
                  {showWeeks ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                {showWeeks && (
                  <div className="space-y-3" data-testid="dietary-daily-totals-list">
                    {weeks.map(wk => (
                      <div key={wk.weekKey} className="rounded-lg border border-amber-200 overflow-hidden">
                        <div className="bg-amber-100 px-3 py-1.5 text-[11px] font-bold text-amber-900 flex items-center justify-between">
                          <span>{wk.label}</span>
                          <span className="text-amber-700">
                            {wk.rows.reduce((s, r) => s + r.total_expected, 0).toLocaleString()} camper-days
                          </span>
                        </div>
                        <div className="divide-y divide-amber-100">
                          {wk.rows.map(row => (
                            <div
                              key={row.date}
                              className="flex items-center justify-between px-3 py-2 hover:bg-amber-50"
                              data-testid={`dietary-day-${row.date}`}
                            >
                              <div>
                                <p className="text-sm font-semibold text-slate-800">{formatDate(row.date)}</p>
                                <p className="text-[10px] text-muted-foreground font-mono">{row.date}</p>
                              </div>
                              <div className="text-right">
                                <p className="text-lg font-extrabold text-amber-800">{row.total_expected.toLocaleString()}</p>
                                <p className="text-[10px] text-amber-700">expected</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* ── Mariner Trips ── */}
              <TabsContent value="mariners" className="mt-3">
                <TripList rows={data.trips?.mariners || []} formatDate={formatDate} accent="rose" subtitleNote="Mariners off-site: G08–G11 on 7/27, B08–B11 on 8/3" />
              </TabsContent>

              {/* ── CIT Trips ── */}
              <TabsContent value="cits" className="mt-3">
                <TripList rows={data.trips?.cits || []} formatDate={formatDate} accent="violet" subtitleNote="CITs off-site: 7/1, 7/7, 7/11, 7/13, 7/23, 7/27, 8/4" />
              </TabsContent>

              {/* ── Navigator Trips ── */}
              <TabsContent value="navigators" className="mt-3">
                <TripList rows={data.trips?.navigators || []} formatDate={formatDate} accent="sky" subtitleNote="Navigators off-site every Tuesday & Thursday (B12–B15, G12–G15)" />
              </TabsContent>
            </Tabs>
          </div>
        )}

        <div className="flex justify-end mt-3 pt-3 border-t">
          <Button variant="outline" size="sm" onClick={onClose} data-testid="dietary-daily-totals-close">
            <X className="w-3.5 h-3.5 mr-1" /> Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function DietaryLanding({ onLogout }) {
  const [showTotals, setShowTotals] = useState(false);
  const token = localStorage.getItem("token");

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-orange-50" data-testid="dietary-landing">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-amber-700 text-white px-4 py-3 shadow-md">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <h1 className="text-lg font-bold flex items-center gap-2">
            <Apple className="w-6 h-6" />
            Dietary Dashboard
          </h1>
          <Button
            variant="ghost"
            size="sm"
            onClick={onLogout}
            className="text-white hover:bg-amber-600 text-xs"
            data-testid="dietary-logout"
          >
            Logout
          </Button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-10 space-y-6">
        <div>
          <h2 className="text-base font-bold text-amber-900">Choose a workspace</h2>
          <p className="text-sm text-amber-800/80 mt-1">Each card opens in a new tab.</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <a
            href="https://rrdcallergen.com"
            target="_blank"
            rel="noopener noreferrer"
            className="group flex flex-col items-start gap-3 p-5 bg-white rounded-2xl border-2 border-amber-200 hover:border-red-400 hover:shadow-lg transition-all"
            data-testid="dietary-card-allergies"
          >
            <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center text-red-700 group-hover:bg-red-200 transition-colors">
              <Wheat className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-bold text-gray-900">Allergies</h3>
                <ExternalLink className="w-3.5 h-3.5 text-gray-400 group-hover:text-red-600 transition-colors" />
              </div>
              <p className="text-xs text-gray-600 mt-1">rrdcallergens.com</p>
            </div>
          </a>

          <a
            href="https://rrdcdining.com"
            target="_blank"
            rel="noopener noreferrer"
            className="group flex flex-col items-start gap-3 p-5 bg-white rounded-2xl border-2 border-amber-200 hover:border-amber-500 hover:shadow-lg transition-all"
            data-testid="dietary-card-dining"
          >
            <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center text-amber-700 group-hover:bg-amber-200 transition-colors">
              <Utensils className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-bold text-gray-900">Dining</h3>
                <ExternalLink className="w-3.5 h-3.5 text-gray-400 group-hover:text-amber-700 transition-colors" />
              </div>
              <p className="text-xs text-gray-600 mt-1">rrdcdining.com</p>
            </div>
          </a>

          <a
            href="https://rrdcstaffsnapshot.com"
            target="_blank"
            rel="noopener noreferrer"
            className="group flex flex-col items-start gap-3 p-5 bg-white rounded-2xl border-2 border-amber-200 hover:border-emerald-500 hover:shadow-lg transition-all"
            data-testid="dietary-card-staff-snapshot"
          >
            <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center text-emerald-700 group-hover:bg-emerald-200 transition-colors">
              <Shirt className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-bold text-gray-900">Staff Snapshot</h3>
                <ExternalLink className="w-3.5 h-3.5 text-gray-400 group-hover:text-emerald-700 transition-colors" />
              </div>
              <p className="text-xs text-gray-600 mt-1">rrdcstaffsnapshot.com</p>
            </div>
          </a>

          <button
            type="button"
            onClick={() => setShowTotals(true)}
            className="group flex flex-col items-start gap-3 p-5 bg-white rounded-2xl border-2 border-amber-200 hover:border-amber-600 hover:shadow-lg transition-all text-left"
            data-testid="dietary-card-daily-totals"
          >
            <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center text-amber-700 group-hover:bg-amber-200 transition-colors">
              <Users className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-bold text-gray-900">Daily Total Campers</h3>
              </div>
              <p className="text-xs text-gray-600 mt-1">6/29 – 8/20 · expected counts per day</p>
            </div>
          </button>
        </div>
      </div>

      {showTotals && <DailyTotalsModal token={token} onClose={() => setShowTotals(false)} />}
    </div>
  );
}
